import { UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT, type UpdateCustomer } from '@weft/types';
import { isEmptyString, isNotNullOrUndefined } from '@weft/utils';
import { VayuTextField } from 'apps/app/src/components/design/vayu-theme-components/VayuTextField';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { NetSuiteCustomersDropdown } from '../../customers-component/NetSuiteCustomersDropdown';
import { CustomerViewPropertiesBlock } from './CustomerViewPropertiesBlock';

type CustomerInfoViewEditNameProps = {
  isNetSuiteEnabled: boolean;
};

export const CustomerInfoViewEditName: React.FC<CustomerInfoViewEditNameProps> = ({ isNetSuiteEnabled }) => {
  const { getValues, register, getFieldState, resetField, trigger, control, setValue } = useFormContext<UpdateCustomer>();
  const [isInEditMode, setIsInEditMode] = useState(false);

  const taxIdRegister = vyFormRegister(register, 'taxId');
  const nameRegister = register('name');
  const crmIdRegister = vyFormRegister(register, 'crmId');
  const erpMetadataRegister = register('erpMetadata.erpId');
  const customerIdRegister = vyFormRegister(register, 'externalId');

  const handleCancelClick = () => {
    resetField('taxId');
    resetField('name');
    resetField('crmId');
    resetField('erpMetadata');
    resetField('externalId');
  };

  const handleSaveClick = () => {
    const erpId = getValues('erpMetadata')?.erpId;

    if (isEmptyString(erpId)) {
      setValue('erpMetadata', undefined);
    }

    return trigger(['taxId', 'name', 'crmId', 'erpMetadata', 'externalId']);
  };

  const onManualErpIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const erpId = event.target.value;

    return setValue('erpMetadata', { erpId, erpName: 'NetSuite Customer' });
  };

  return (
    <CustomerViewPropertiesBlock
      title='Details'
      showEditButton
      onEditChange={setIsInEditMode}
      sectionToSubmit={UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.details}
      onCancel={handleCancelClick}
      onSave={handleSaveClick}
    >
      <div className='flex w-1/5 flex-col border-0 pr-6'>
        <VayuTextField
          label='Name'
          {...nameRegister}
          focused={isInEditMode}
          disabled={!isInEditMode}
          defaultValue={getValues('name')}
          placeholder="Text here"
          error={isNotNullOrUndefined(getFieldState('name')?.error)}
          helperText={getFieldState('name')?.error?.message}
        />
      </div>

      <div className='flex w-1/5 flex-col border-0 pr-6'>
        <VayuTextField
          label='Alias'
          {...customerIdRegister}
          focused={isInEditMode}
          disabled={!isInEditMode}
          defaultValue={getValues('externalId')}
          placeholder="Text here"
          error={isNotNullOrUndefined(getFieldState('externalId')?.error)}
          helperText={getFieldState('externalId')?.error?.message}
        />
      </div>

      <div className='flex w-1/5 flex-col border-0 pr-6'>
        <VayuTextField
          label='Tax ID'
          {...taxIdRegister}
          focused={isInEditMode}
          disabled={!isInEditMode}
          defaultValue={getValues('taxId')}
          placeholder="Text here"
          error={isNotNullOrUndefined(getFieldState('taxId')?.error)}
          helperText={getFieldState('taxId')?.error?.message}
        />
      </div>

      <div className='flex w-1/5 flex-col border-0 pr-6'>
        <VayuTextField
          label='CRM ID'
          {...crmIdRegister}
          defaultValue={getValues('crmId') ?? undefined}
          focused={isInEditMode}
          disabled={!isInEditMode}
          placeholder="Text here"
          error={isNotNullOrUndefined(getFieldState('crmId')?.error)}
          helperText={getFieldState('crmId')?.error?.message}
        />
      </div>
      <div className='group flex w-1/5 flex-col border-0 pr-6 relative'>
        {isNetSuiteEnabled ? 
          <div
            className={`absolute w-[200px] ${
              isInEditMode ? 'top-[-10px]' : 'top-[-15px]'
            }`}
          >
            <NetSuiteCustomersDropdown
              control={control}
              showDropdown={isInEditMode}
              currentErpCustomer={getValues('erpMetadata')}
            />
          </div>
          : 
          <VayuTextField
            label='ERP ID'
            {...erpMetadataRegister}
            onChange={onManualErpIdChange}
            focused={isInEditMode}
            disabled={!isInEditMode}
            placeholder="Text here"
            error={isNotNullOrUndefined(getFieldState('erpMetadata.erpId')?.error)}
            helperText={getFieldState('erpMetadata.erpId')?.error?.message}
          />
        } 

      </div>
    </CustomerViewPropertiesBlock>
  );
};